import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { File as FileIcon, FileAudio, FileImage, FileVideo, Undo2, X } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, api, type MailDetail, type MailRecipientOption, type UserRef } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { formatBytes, formatDateTime, formatNumber } from '../lib/format';
import { FormPage } from '../components/FormPage';
import { AttachmentsField, useUploads } from '../components/Attachments';
import { PeoplePicker } from '../components/PeoplePicker';
import { RichTextEditor } from '../components/RichTextLazy';
import { Field, Input, useToast } from '../components/ui';

/** The API's own limits (modules/mailbox/mail-rules.ts). */
const MAX_RECIPIENTS = 100;
const MAX_SUBJECT = 200;

type Mode = 'new' | 'reply' | 'replyAll' | 'forward';

const REPLY_PREFIX = /^\s*(re|رد)\s*:/i;
const FORWARD_PREFIX = /^\s*(fwd?|إعادة توجيه)\s*:/i;

/** "Re: Budget" — once, however many times the mail goes back and forth. */
function replySubject(subject: string): string {
  return (REPLY_PREFIX.test(subject) ? subject : `Re: ${subject}`).slice(0, MAX_SUBJECT);
}

function forwardSubject(subject: string): string {
  return (FORWARD_PREFIX.test(subject) ? subject : `Fwd: ${subject}`).slice(0, MAX_SUBJECT);
}

/** Text going into the editor's HTML — a name, a date, a subject — as text, never as markup. */
function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function iconFor(mime: string) {
  if (mime.startsWith('image/')) return FileImage;
  if (mime.startsWith('audio/')) return FileAudio;
  if (mime.startsWith('video/')) return FileVideo;
  return FileIcon;
}

/**
 * Route: `/mail/new` — a mail to other employees (operator, 2026-10-09): to
 * whom, a subject, the content in the rich-text editor, and files and voice
 * notes. Inside the system only: nobody gets an email, the bell tells them.
 *
 * The same page answers and passes on a mail: `?reply=<id>` (to whoever sent
 * it), `?replyAll=<id>` (to everyone else on it) and `?forward=<id>` (to whom
 * you choose, with its files). It starts with the subject and the quoted mail
 * filled in, all of it editable. `?to=<id>` starts a new mail to someone — an
 * employee's page has the button.
 */
export function ComposeMailPage() {
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const uploads = useUploads();
  const [params] = useSearchParams();
  const toId = useId();

  const mode: Mode = params.get('reply')
    ? 'reply'
    : params.get('replyAll')
      ? 'replyAll'
      : params.get('forward')
        ? 'forward'
        : 'new';
  const sourceId = params.get('reply') ?? params.get('replyAll') ?? params.get('forward');

  const people = useQuery({
    queryKey: ['mailbox', 'recipients'],
    queryFn: () => api.get<MailRecipientOption[]>('/mailbox/recipients'),
    staleTime: 60_000,
  });
  // Read without reading it: answering a mail does not mark it read for you.
  const source = useQuery({
    queryKey: ['mail', sourceId, 'source'],
    queryFn: () => api.get<MailDetail>(`/mailbox/${sourceId}`, { registerView: false }),
    enabled: !!sourceId,
  });

  const [recipientIds, setRecipientIds] = useState<string[]>(() => {
    const to = params.get('to');
    return mode === 'new' && to ? [to] : [];
  });
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [leftOut, setLeftOut] = useState<string[]>([]);
  const [prefilled, setPrefilled] = useState(false);
  // Each field's warning waits until it has been left, or Send was pressed.
  const [touched, setTouched] = useState({ to: false, subject: false });

  // The answer starts from the mail it answers, once that has loaded.
  if (source.data && !prefilled) {
    setPrefilled(true);
    const m = source.data;
    const when = formatDateTime(m.createdAt, locale);
    const from = localized(m.sender);
    if (mode === 'forward') {
      const to = m.recipients.map((p) => localized(p)).join(locale === 'ar' ? '، ' : ', ');
      const lines = [
        t('mail.forwardHeader'),
        t('mail.forwardFrom', { name: from }),
        t('mail.forwardDate', { date: when }),
        t('mail.forwardSubject', { subject: m.subject }),
        t('mail.forwardTo', { names: to }),
      ];
      setSubject(forwardSubject(m.subject));
      setBody(`<p></p><p dir="auto">${lines.map(escapeHtml).join('<br>')}</p>${m.bodyHtml ?? ''}`);
    } else {
      setRecipientIds(mode === 'reply' ? m.targets.reply : m.targets.replyAll);
      setSubject(replySubject(m.subject));
      setBody(
        m.bodyHtml
          ? `<p></p><p dir="auto">${escapeHtml(t('mail.quoteHeader', { date: when, name: from }))}</p><blockquote>${m.bodyHtml}</blockquote>`
          : '',
      );
    }
  }

  // Only active employees other than you can be written to: a `?to=` naming
  // anyone else is dropped once the list of people is in.
  const offered = people.data;
  useEffect(() => {
    if (!offered) return;
    const ids = new Set(offered.map((p) => p.id));
    setRecipientIds((current) => (current.every((id) => ids.has(id)) ? current : current.filter((id) => ids.has(id))));
  }, [offered]);

  // The people on the mail being answered, for their names while the list loads.
  const known = useMemo(() => {
    const map = new Map<string, UserRef>();
    if (source.data) for (const p of [source.data.sender, ...source.data.recipients]) map.set(p.id, p);
    return map;
  }, [source.data]);

  // An answer leaves out whoever is no longer an employee; say who, so nobody wonders.
  const gone =
    source.data && mode !== 'forward'
      ? [source.data.sender, ...source.data.recipients]
          .filter((p) => !p.isActive)
          .filter((p) => mode === 'replyAll' || p.id === source.data!.sender.id)
          .map((p) => localized(p))
      : [];

  const passedOn = (mode === 'forward' ? (source.data?.attachments ?? []) : []).filter(
    (a) => !leftOut.includes(a.file.id),
  );

  const needRecipient = recipientIds.length === 0;
  const needSubject = !subject.trim();
  const ready = !needRecipient && !needSubject && !uploads.busy && (!sourceId || !!source.data);

  const send = useMutation({
    mutationFn: () =>
      api.post<MailDetail>('/mailbox', {
        recipientIds,
        subject,
        bodyHtml: body || undefined,
        fileIds: uploads.fileIds.length ? uploads.fileIds : undefined,
        ...(mode === 'reply' || mode === 'replyAll' ? { replyToId: sourceId } : {}),
        ...(mode === 'forward'
          ? { forwardOfId: sourceId, forwardFileIds: passedOn.length ? passedOn.map((a) => a.file.id) : undefined }
          : {}),
      }),
    onSuccess: (sent) => {
      void queryClient.invalidateQueries({ queryKey: ['mailbox'] });
      queryClient.setQueryData(['mail', sent.id], sent);
      const n = sent.notified ?? recipientIds.length;
      toast.success(n > 1 ? t('mail.sentTo', { n: formatNumber(n, locale) }) : t('mail.sent'));
      // In place of the form, so going back from the mail does not reopen it.
      navigate(`/mail/${sent.id}`, { replace: true });
    },
    onError: (e: Error) => {
      if (e instanceof ApiError) {
        const details = e.details as { field?: string; reason?: string } | undefined;
        if (details?.field === 'recipientIds' && details.reason === 'INACTIVE') return toast.error(t('mail.notEmployee'));
        if (details?.field === 'bodyHtml') return toast.error(t('mail.tooLong'));
        if (e.code === 'RESOURCE_NOT_FOUND') return toast.error(t('mail.sourceMissing'));
      }
      toast.error(e.message);
    },
  });

  const title = mode === 'new' ? t('mail.new') : mode === 'forward' ? t('mail.forwardTitle') : t('mail.replyTitle');

  return (
    <FormPage
      title={title}
      subtitle={t('mail.composeSubtitle')}
      backTo={sourceId ? `/mail/${sourceId}` : '/mail'}
      backLabel={source.data?.subject ?? t('mail.title')}
      submitLabel={t('mail.send')}
      submitting={send.isPending}
      // Sending while a file is still uploading would leave it behind.
      disabled={!ready}
      loading={!!sourceId && source.isLoading}
      error={source.error}
      onRetry={() => void source.refetch()}
      onSubmit={() => {
        setTouched({ to: true, subject: true });
        if (ready) send.mutate();
      }}
      width="lg"
    >
      <div className="space-y-5">
        {/* Not a Field: that is a <label>, and a label around the chips' buttons would press them. */}
        <div onBlur={() => setTouched((was) => ({ ...was, to: true }))}>
          <label htmlFor={toId} className="mb-1.5 flex items-center gap-1 text-body-sm font-medium text-[var(--text-secondary)]">
            {t('mail.toLabel')}
            <span className="text-primary dark:text-primary-400">*</span>
          </label>
          <PeoplePicker
            inputId={toId}
            people={people.data}
            value={recipientIds}
            onChange={setRecipientIds}
            max={MAX_RECIPIENTS}
            known={known}
            invalid={touched.to && needRecipient}
          />
          {touched.to && needRecipient ? (
            <span className="mt-1 block text-caption text-warning-fg dark:text-warning-dark">{t('mail.needRecipient')}</span>
          ) : (
            <span className="mt-1 block text-caption text-faint">{t('mail.toHint')}</span>
          )}
          {gone.length > 0 && (
            <span className="mt-1 block text-caption text-muted">
              {t('mail.someoneLeft', { names: gone.join(locale === 'ar' ? '، ' : ', ') })}
            </span>
          )}
        </div>

        <Field label={t('mail.subject')} required error={touched.subject && needSubject ? t('mail.needSubject') : null}>
          <Input
            value={subject}
            maxLength={MAX_SUBJECT}
            dir="auto"
            onChange={(e) => setSubject(e.target.value)}
            onBlur={() => setTouched((was) => ({ ...was, subject: true }))}
          />
        </Field>

        <div>
          <span className="mb-1.5 block text-body-sm font-medium text-[var(--text-secondary)]">{t('mail.content')}</span>
          <RichTextEditor value={body} onChange={setBody} label={t('mail.content')} />
        </div>

        <AttachmentsField uploads={uploads} />

        {mode === 'forward' && (source.data?.attachments.length ?? 0) > 0 && (
          <div>
            <span className="mb-1.5 block text-body-sm font-medium text-[var(--text-secondary)]">{t('mail.passedOn')}</span>
            <ul className="space-y-1.5">
              {source.data!.attachments.map((a) => {
                const out = leftOut.includes(a.file.id);
                const Icon = iconFor(a.file.mimeType);
                return (
                  <li
                    key={a.id}
                    className={clsx('flex items-center gap-3 rounded-md border hairline px-3 py-2', out && 'opacity-60')}
                  >
                    <Icon className="h-5 w-5 shrink-0 text-primary-600 dark:text-primary-400" aria-hidden />
                    <div className="min-w-0 flex-1">
                      <p className={clsx('truncate text-body-sm font-medium text-[var(--text-primary)]', out && 'line-through')}>
                        <bdi>{a.file.kind === 'VOICE_NOTE' ? t('attachments.voiceNote') : a.file.originalName}</bdi>
                      </p>
                      <p className="text-caption text-muted">
                        {formatBytes(a.file.sizeBytes, locale)}
                        {out && ` · ${t('mail.leftOut')}`}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setLeftOut((ids) => (out ? ids.filter((x) => x !== a.file.id) : [...ids, a.file.id]))
                      }
                      className="inline-flex shrink-0 items-center gap-1 rounded-sm px-2 py-1 text-caption font-medium text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
                    >
                      {out ? <Undo2 className="h-3.5 w-3.5 flip-rtl" aria-hidden /> : <X className="h-3.5 w-3.5" aria-hidden />}
                      {out ? t('mail.putBack') : t('mail.leaveOut')}
                    </button>
                  </li>
                );
              })}
            </ul>
            <span className="mt-1 block text-caption text-faint">{t('mail.passedOnHint')}</span>
          </div>
        )}
      </div>
    </FormPage>
  );
}
